import { Head, useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'

interface RegisterProps {
  locked_email?: string
  invitation_token?: string
  errors?: {
    name?: string[]
    email_address?: string[]
    password?: string[]
    password_confirmation?: string[]
  }
}

export default function Register({ locked_email, invitation_token, errors }: RegisterProps) {
  const { data, setData, post, processing } = useForm({
    user: {
      name: '',
      email_address: locked_email || '',
      password: '',
      password_confirmation: '',
    },
    invitation_token: invitation_token || '',
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/register')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12 sm:px-6 lg:px-8">
      <Head title="Register" />
      <div className="w-full max-w-md space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link href="/session/new" className="font-medium text-indigo-600 hover:text-indigo-500">
              Log in
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4 rounded-md">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                id="name"
                name="user[name]"
                type="text"
                autoComplete="name"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                value={data.user.name}
                onChange={(e) => setData('user', { ...data.user, name: e.target.value })}
              />
              {errors?.name && <p className="mt-1 text-sm text-red-600">{errors.name[0]}</p>}
            </div>

            <div>
              <label htmlFor="email_address" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="email_address"
                name="user[email_address]"
                type="email"
                autoComplete="email"
                required
                readOnly={!!locked_email}
                className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${locked_email ? 'bg-gray-100 text-gray-500' : ''}`}
                value={data.user.email_address}
                onChange={(e) => setData('user', { ...data.user, email_address: e.target.value })}
              />
              {errors?.email_address && (
                <p className="mt-1 text-sm text-red-600">{errors.email_address[0]}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                id="password"
                name="user[password]"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                value={data.user.password}
                onChange={(e) => setData('user', { ...data.user, password: e.target.value })}
              />
              {errors?.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password[0]}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="password_confirmation"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm password
              </label>
              <input
                id="password_confirmation"
                name="user[password_confirmation]"
                type="password"
                autoComplete="new-password"
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                value={data.user.password_confirmation}
                onChange={(e) =>
                  setData('user', { ...data.user, password_confirmation: e.target.value })
                }
              />
              {errors?.password_confirmation && (
                <p className="mt-1 text-sm text-red-600">{errors.password_confirmation[0]}</p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={processing}
              className="group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
            >
              Create account
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
